//
// Definitions
//

/** Token Patterns - same as from npm vue-the-mask */
const TOKENS = {
  '#': {pattern: /\d/},
  'X': {pattern: /[0-9a-zA-Z]/},
  'S': {pattern: /[a-zA-Z]/},
  'A': {pattern: /[a-zA-Z]/, transform: v => v.toLocaleUpperCase()},
  'a': {pattern: /[a-zA-Z]/, transform: v => v.toLocaleLowerCase()},
  '!': {escape: true}
};

//
// Public Interface
//

/**
 * A Vue directive for masking string input.
*/
export default {
  mounted(element, binding) {
    const inputElement = firstInputChild(element);
    if (!inputElement) {
      return;
    }

    inputElement.dataset.mask = binding.value;

    const onInputHandler = (event) => {
      if (!event.isTrusted || !inputElement.dataset.mask) {
        return;
      }
      if (event._maskTriggered) return;

      let currentVal = inputElement.value;
      let selectionStart = inputElement.selectionStart;

      let maskedInput = applyMask(currentVal, inputElement.dataset.mask);
      let maskedPartial = applyMask(currentVal.substring(0, selectionStart), inputElement.dataset.mask);

      let delta = maskedPartial.length - selectionStart;
      let newCursor = Math.max(0, Math.min(selectionStart + delta, maskedInput.length));

      if (maskedInput !== currentVal) {
        inputElement.value = maskedInput;
      }

      inputElement.setSelectionRange(newCursor, newCursor);

      requestAnimationFrame(() => {
        const simulatedEvent = cloneEvent(event);
        if (simulatedEvent) {
          simulatedEvent._maskTriggered = true;
          inputElement.dispatchEvent(simulatedEvent);
        }
      });
    };

    inputElement._mask_onInput = onInputHandler;
    inputElement.addEventListener('input', inputElement._mask_onInput);
  },

  updated(element, binding) {
    const inputElement = firstInputChild(element);
    if (!inputElement || binding.value === binding.oldValue) return;

    inputElement.dataset.mask = binding.value;

    if (inputElement._mask_onInput && inputElement.value) {
      let currentVal = inputElement.value;
      let maskedInput = applyMask(currentVal, inputElement.dataset.mask);
      if (inputElement.value !== maskedInput) {
        inputElement.value = maskedInput;
      }
    }
  },

  unmounted(element) {
    const inputElement = firstInputChild(element);
    if (inputElement && inputElement._mask_onInput) {
      inputElement.removeEventListener('input', inputElement._mask_onInput);
      delete inputElement._mask_onInput;
    }
  }
};

//
// Implementation
//

/**
 * Clone a DOM event and initialize it so it may be dispatched.
 *
 * @param {Event} e a DOM event object
 * @returns {Event} a newly initialized DOM event
 */
function cloneEvent(e) {
  var clone = document.createEvent("HTMLEvents");
  clone.initEvent(e.type, e.bubbles, e.cancelable);
  return clone;
}

/**
 * Return the first child of the given element that is an Input element,
 * or the element if it is itself one.
 *
 * @param {HTMLElement} element a DOM element
 * @returns {HTMLInputElement} an DOM Input element
 * @throws {Error} if no Input element is found
 */
function firstInputChild(element) {
  if (element.tagName.toLocaleUpperCase() == "INPUT") {
    return element;
  }

  var inputs = element.getElementsByTagName("input");
  if (!inputs.length) {
    throw new Error("Mask Directive requires an HTMLInputElement child.");
  }

  return inputs[0];
}

/**
 * Apply a token mask to the given string.
 *
 * Implementation from npm vue-the-mask:
 * @link https://www.npmjs.com/package/vue-the-mask
 *
 * @param {String} input the string to mask
 * @param {String} mask the mask format
 * @returns {String} the masked string result
 */
function applyMask(input, mask) {
  input = input || "";
  mask = mask || "";
  let iMask = 0;
  let iValue = 0;
  let output = "";

  while (iMask < mask.length && iValue < input.length) {
    let cMask = mask[iMask];
    let masker = TOKENS[cMask];
    let cValue = input[iValue];

    if (masker && !masker.escape) {
      if (masker.pattern.test(cValue)) {
        // transform if set
        output += masker.transform ? masker.transform(cValue) : cValue;
        iMask++;
      }
      iValue++;
    } else {
      if (masker && masker.escape) {
        iMask++;
        cMask = mask[iMask];
      }
      output += cMask;

      if (cValue === cMask) {
        iValue++;
      }

      iMask++;
    }
  }

  let restOutput = "";
  while (iMask < mask.length) {
    let cMask = mask[iMask];
    if (TOKENS[cMask]) {
      restOutput = "";
      break;
    }
    restOutput += cMask;
    iMask++;
  }

  let result = output + restOutput;
  return result;
}

